import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { projectService } from '../services/projectService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';

type ProjectType = 'fixed' | 'auction' | 'opensource';
type RevenueStatus = 'pre-revenue' | 'established' | 'na';

interface ProjectForm {
  // Step 1: Basic Information
  title: string;
  shortDescription: string;
  tags: string[];
  category: string;
  thumbnail: File | null;
  imageUrl?: string;

  // Step 2: Project Details
  fullDescription: string;
  technologies: string[];
  projectType: ProjectType;
  revenueStatus: RevenueStatus;
  monthlyRevenue?: number;
  annualRevenue?: number;
  revenueType?: 'mrr' | 'arr';
  userCount?: number;
  ytdGrowth?: number;

  // Step 3: Pricing & Availability
  price?: number;
  auctionSettings?: {
    startingBid: number;
    reservePrice?: number;
    buyNowPrice?: number;
    duration: number; // in days
  };
  licensing: {
    includeSourceCode: boolean;
    transferOwnership: boolean;
  };
}

const steps = [
  { id: 1, name: 'Basic Information' },
  { id: 2, name: 'Project Details' },
  { id: 3, name: 'Pricing & Availability' },
  { id: 4, name: 'Review & Publish' },
];

export const SellProject = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectForm>({
    title: '',
    shortDescription: '',
    tags: [],
    category: '',
    thumbnail: null,
    fullDescription: '',
    technologies: [],
    projectType: 'fixed',
    revenueStatus: 'pre-revenue',
    revenueType: undefined,
    monthlyRevenue: undefined,
    annualRevenue: undefined,
    userCount: undefined,
    ytdGrowth: undefined,
    price: undefined,
    auctionSettings: {
      startingBid: 0,
      reservePrice: undefined,
      buyNowPrice: undefined,
      duration: 7,
    },
    licensing: {
      includeSourceCode: true,
      transferOwnership: true,
    },
  });

  useEffect(() => {
    const setAuthToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        localStorage.setItem('auth0_token', token);
      } catch (error) {
        console.error('Error setting auth token:', error);
      }
    };

    if (isAuthenticated) {
      setAuthToken();
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    }
  }, [isAuthenticated, loginWithRedirect]);

  const updateForm = (updates: Partial<ProjectForm>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const loadingToast = toast.loading('Creating your project...');

      // Ensure we have a fresh token
      const token = await getAccessTokenSilently();
      localStorage.setItem('auth0_token', token);
      console.log('Auth token refreshed:', !!token);

      // First upload the image if it exists
      let imageUrl;
      if (form.thumbnail) {
        toast.loading('Uploading image...', { id: loadingToast });
        try {
          imageUrl = await projectService.uploadImage(form.thumbnail);
          console.log('Image uploaded successfully:', imageUrl);
        } catch (uploadError: any) {
          console.error('Error uploading image:', uploadError);
          if (uploadError.response?.status === 401) {
            // If we get a 401, try to refresh the token and retry once
            const newToken = await getAccessTokenSilently();
            localStorage.setItem('auth0_token', newToken);
            imageUrl = await projectService.uploadImage(form.thumbnail);
          } else {
            throw uploadError;
          }
        }
        toast.dismiss(loadingToast);
      }

      // Create the project with the image URL if one was uploaded
      const projectData = {
        title: form.title,
        description: form.fullDescription,
        shortDescription: form.shortDescription,
        imageUrl,
        price: form.projectType === 'fixed' ? form.price : undefined,
        projectType: form.projectType,
        revenueStatus: form.revenueStatus === 'established' ? 'revenue' : form.revenueStatus,
        monthlyRevenue: form.revenueType === 'mrr' ? form.monthlyRevenue : undefined,
        annualRevenue: form.revenueType === 'arr' ? form.annualRevenue : undefined,
        userCount: form.userCount,
        ytdGrowth: form.ytdGrowth,
        licensing: form.licensing,
        auctionSettings: form.projectType === 'auction' ? form.auctionSettings : undefined,
        category: form.category,
        tags: form.tags,
      };

      console.log('Submitting project data:', projectData);
      const project = await projectService.createProject(projectData);
      
      toast.dismiss(loadingToast);
      toast.success('Project created successfully!');
      
      // Wait a moment to show the success message
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Navigate to the project page
      navigate(`/projects/${project._id}`);
    } catch (err: any) {
      console.error('Error creating project:', err);
      toast.dismiss();
      
      if (err.response?.status === 401) {
        toast.error('Please sign in to create a project');
        loginWithRedirect({
          appState: { returnTo: window.location.pathname }
        });
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message, { duration: 5000 });
      } else if (err.message) {
        toast.error(err.message, { duration: 5000 });
      } else {
        toast.error('Failed to create project. Please try again.', { duration: 5000 });
      }
      
      // Log the full error response for debugging
      if (err.response) {
        console.error('Error response:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-12">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Tell us about your project</h2>
              <p className="text-xl text-gray-600">
                Let's start with the basics. You can always save and come back later.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <label htmlFor="title" className="block text-base font-semibold text-gray-700 mb-2">
                  Project Title
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="title"
                    value={form.title}
                    onChange={(e) => updateForm({ title: e.target.value })}
                    className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                    placeholder="E.g., 'AI-Powered Content Generator'"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="shortDescription" className="block text-base font-semibold text-gray-700 mb-2">
                  Short Description
                </label>
                <div className="mt-1">
                  <textarea
                    id="shortDescription"
                    value={form.shortDescription}
                    onChange={(e) => updateForm({ shortDescription: e.target.value })}
                    rows={4}
                    className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                    placeholder="A brief, compelling description of your project (150 characters max)"
                    maxLength={150}
                  />
                  <p className="mt-3 text-sm text-gray-500 flex items-center">
                    <span className={`${form.shortDescription.length >= 140 ? 'text-amber-500' : ''}`}>
                      {form.shortDescription.length}
                    </span>
                    <span className="mx-1">/</span>
                    <span>150 characters</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <div className="mt-1">
                  <select
                    value={form.category}
                    onChange={(e) => updateForm({ category: e.target.value })}
                    className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                  >
                    <option value="">Select a category</option>
                    <option value="ai-ml">AI & ML</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="tools">Tools & Utilities</option>
                    <option value="games">Game Projects</option>
                    <option value="open-source">Open Source</option>
                    <option value="content">Content & Media</option>
                    <option value="saas">SaaS</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Tags
                </label>
                <div className="mt-1">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => {
                            const newTags = [...form.tags];
                            newTags.splice(index, 1);
                            updateForm({ tags: newTags });
                          }}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a tag"
                      className="flex-1 rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          e.preventDefault();
                          const newTag = e.currentTarget.value.trim();
                          if (!form.tags.includes(newTag)) {
                            updateForm({
                              tags: [...form.tags, newTag]
                            });
                          }
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                        if (input.value.trim()) {
                          const newTag = input.value.trim();
                          if (!form.tags.includes(newTag)) {
                            updateForm({
                              tags: [...form.tags, newTag]
                            });
                          }
                          input.value = '';
                        }
                      }}
                      className="px-4 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                    >
                      Add
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Press Enter or click Add to add a tag. Maximum 5 tags allowed.
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="thumbnail" className="block text-base font-semibold text-gray-700 mb-2">
                  Project Thumbnail
                </label>
                <div 
                  className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-primary-500 transition-colors duration-200"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      if (file.size > 10 * 1024 * 1024) { // 10MB limit
                        toast.error('Image size must be less than 10MB');
                        return;
                      }
                      updateForm({ thumbnail: file });
                    } else {
                      toast.error('Please upload a valid image file');
                    }
                  }}
                >
                  <div className="space-y-2 text-center">
                    {form.thumbnail ? (
                      <div className="relative w-full max-w-md mx-auto">
                        <img
                          src={URL.createObjectURL(form.thumbnail)}
                          alt="Thumbnail preview"
                          className="rounded-lg max-h-48 mx-auto object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => updateForm({ thumbnail: null })}
                          className="absolute top-2 right-2 p-1.5 bg-gray-900/50 rounded-full text-white hover:bg-gray-900/75 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex justify-center text-sm text-gray-600">
                          <label
                            htmlFor="thumbnail-upload"
                            className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                          >
                            <span>Upload a file</span>
                            <input
                              id="thumbnail-upload"
                              name="thumbnail-upload"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.size > 10 * 1024 * 1024) { // 10MB limit
                                    toast.error('Image size must be less than 10MB');
                                    return;
                                  }
                                  updateForm({ thumbnail: file });
                                }
                              }}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-12">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Project Details</h2>
              <p className="text-xl text-gray-600">
                Help potential buyers understand your project better.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <label htmlFor="fullDescription" className="block text-base font-semibold text-gray-700 mb-2">
                  Full Description
                </label>
                <div className="mt-1">
                  <textarea
                    id="fullDescription"
                    value={form.fullDescription}
                    onChange={(e) => updateForm({ fullDescription: e.target.value })}
                    rows={8}
                    className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                    placeholder="Describe your project in detail. Include features, technical details, and any other relevant information."
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">Project Type</label>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['fixed', 'auction', 'opensource'].map((type) => (
                    <button
                      key={type}
                      onClick={() => updateForm({ projectType: type as ProjectType })}
                      className={`p-4 border-2 rounded-xl text-left transition-all duration-200 ${
                        form.projectType === type
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold capitalize">{type}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {type === 'fixed' && 'Set a fixed price for your project'}
                        {type === 'auction' && 'Let buyers bid on your project'}
                        {type === 'opensource' && 'Share your project with the community'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">Revenue Status</label>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { id: 'pre-revenue', label: 'Pre-revenue', desc: 'Not yet generating revenue' },
                    { id: 'established', label: 'Established', desc: 'Already generating revenue' },
                    { id: 'na', label: 'Not Applicable', desc: 'Revenue status not relevant' },
                  ].map((status) => (
                    <button
                      key={status.id}
                      onClick={() => updateForm({ revenueStatus: status.id as RevenueStatus })}
                      className={`p-4 border-2 rounded-xl text-left transition-all duration-200 ${
                        form.revenueStatus === status.id
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold">{status.label}</div>
                      <div className="text-sm text-gray-500 mt-1">{status.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="userCount" className="block text-base font-semibold text-gray-700 mb-2">
                    Number of Users
                  </label>
                  <div className="mt-1 relative rounded-xl shadow-sm">
                    <input
                      type="number"
                      id="userCount"
                      value={form.userCount || ''}
                      onChange={(e) => updateForm({ userCount: parseInt(e.target.value) || undefined })}
                      className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="ytdGrowth" className="block text-base font-semibold text-gray-700 mb-2">
                    Year-to-Date Growth (%)
                  </label>
                  <div className="mt-1 relative rounded-xl shadow-sm">
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <span className="text-gray-500 sm:text-lg">%</span>
                    </div>
                    <input
                      type="number"
                      id="ytdGrowth"
                      value={form.ytdGrowth || ''}
                      onChange={(e) => updateForm({ ytdGrowth: parseFloat(e.target.value) || undefined })}
                      className="block w-full rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                      placeholder="0"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {form.revenueStatus === 'established' && (
                <div className="mt-6 space-y-6">
                  <div>
                    <label className="block text-base font-semibold text-gray-700 mb-2">Revenue Type</label>
                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { id: 'mrr', label: 'Monthly (MRR)', desc: 'Monthly Recurring Revenue' },
                        { id: 'arr', label: 'Annual (ARR)', desc: 'Annual Recurring Revenue' },
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => updateForm({ 
                            revenueType: type.id as 'mrr' | 'arr',
                            monthlyRevenue: type.id === 'mrr' ? form.monthlyRevenue : undefined,
                            annualRevenue: type.id === 'arr' ? form.annualRevenue : undefined,
                          })}
                          className={`p-4 border-2 rounded-xl text-left transition-all duration-200 ${
                            form.revenueType === type.id
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-semibold">{type.label}</div>
                          <div className="text-sm text-gray-500 mt-1">{type.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.revenueType && (
                    <div>
                      <label htmlFor="revenue" className="block text-base font-semibold text-gray-700 mb-2">
                        {form.revenueType === 'mrr' ? 'Monthly Recurring Revenue' : 'Annual Recurring Revenue'}
                      </label>
                      <div className="mt-1 relative rounded-xl shadow-sm max-w-xs">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                          <span className="text-gray-500 sm:text-lg">$</span>
                        </div>
                        <input
                          type="number"
                          id="revenue"
                          value={form.revenueType === 'mrr' ? form.monthlyRevenue || '' : form.annualRevenue || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || undefined;
                            updateForm(form.revenueType === 'mrr' 
                              ? { monthlyRevenue: value, annualRevenue: undefined }
                              : { annualRevenue: value, monthlyRevenue: undefined }
                            );
                          }}
                          className="block w-full rounded-xl border-[1px] border-gray-300 pl-8 pr-16 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <span className="text-gray-500 sm:text-lg">USD</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-12">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Pricing & Availability</h2>
              <p className="text-xl text-gray-600">
                Set your pricing strategy and licensing terms.
              </p>
            </div>

            <div className="space-y-8">
              {form.projectType === 'fixed' && (
                <div>
                  <label htmlFor="price" className="block text-base font-semibold text-gray-700 mb-2">
                    Price
                  </label>
                  <div className="mt-1 relative rounded-xl shadow-sm max-w-xs">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                      <span className="text-gray-500 sm:text-lg">$</span>
                    </div>
                    <input
                      type="number"
                      id="price"
                      value={form.price || ''}
                      onChange={(e) => updateForm({ price: parseFloat(e.target.value) || undefined })}
                      className="block w-full rounded-xl border-[1px] border-gray-300 pl-8 pr-16 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <span className="text-gray-500 sm:text-lg">USD</span>
                    </div>
                  </div>
                </div>
              )}

              {form.projectType === 'auction' && (
                <div className="space-y-6">
                  <div>
                    <label htmlFor="startingBid" className="block text-base font-semibold text-gray-700 mb-2">
                      Starting Bid
                    </label>
                    <div className="mt-1 relative rounded-xl shadow-sm max-w-xs">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                        <span className="text-gray-500 sm:text-lg">$</span>
                      </div>
                      <input
                        type="number"
                        id="startingBid"
                        value={form.auctionSettings?.startingBid || ''}
                        onChange={(e) =>
                          updateForm({
                            auctionSettings: {
                              ...form.auctionSettings!,
                              startingBid: parseFloat(e.target.value) || 0,
                            },
                          })
                        }
                        className="block w-full rounded-xl border-[1px] border-gray-300 pl-8 pr-16 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <span className="text-gray-500 sm:text-lg">USD</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="duration" className="block text-base font-semibold text-gray-700 mb-2">
                      Auction Duration
                    </label>
                    <div className="mt-1">
                      <select
                        id="duration"
                        value={form.auctionSettings?.duration}
                        onChange={(e) =>
                          updateForm({
                            auctionSettings: {
                              ...form.auctionSettings!,
                              duration: parseInt(e.target.value),
                            },
                          })
                        }
                        className="block w-full max-w-xs rounded-xl border-[1px] border-gray-300 px-4 py-3 text-lg ring-0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:outline-none transition-all duration-200"
                      >
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={30}>30 days</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <label className="block text-base font-semibold text-gray-700">Licensing Terms</label>
                <div className="space-y-4">
                  <div className="relative flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="includeSourceCode"
                        type="checkbox"
                        checked={form.licensing.includeSourceCode}
                        onChange={(e) =>
                          updateForm({
                            licensing: { ...form.licensing, includeSourceCode: e.target.checked },
                          })
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-[1px] border-gray-300 rounded transition-colors duration-200"
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="includeSourceCode" className="text-base text-gray-700">
                        Include Source Code
                      </label>
                      <p className="text-sm text-gray-500">
                        The buyer will receive all source code files
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="transferOwnership"
                        type="checkbox"
                        checked={form.licensing.transferOwnership}
                        onChange={(e) =>
                          updateForm({
                            licensing: { ...form.licensing, transferOwnership: e.target.checked },
                          })
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-[1px] border-gray-300 rounded transition-colors duration-200"
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="transferOwnership" className="text-base text-gray-700">
                        Transfer Full Ownership
                      </label>
                      <p className="text-sm text-gray-500">
                        Transfer all rights and ownership to the buyer
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-12">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Review & Publish</h2>
              <p className="text-xl text-gray-600">
                Review your project details before publishing.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-gray-50 rounded-xl p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Project Title</div>
                      <div className="mt-1 text-base text-gray-900">{form.title || '—'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Short Description</div>
                      <div className="mt-1 text-base text-gray-900">{form.shortDescription || '—'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Category</div>
                      <div className="mt-1 text-base text-gray-900">
                        {form.category ? form.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' & ') : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Tags</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {form.tags.length > 0 ? (
                          form.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Project Details</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Project Type</div>
                      <div className="mt-1 text-base text-gray-900 capitalize">{form.projectType}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Revenue Status</div>
                      <div className="mt-1 text-base text-gray-900 capitalize">{form.revenueStatus}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Full Description</div>
                      <div className="mt-1 text-base text-gray-900 whitespace-pre-wrap">
                        {form.fullDescription || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Pricing & Licensing</h3>
                  <div className="mt-4 space-y-4">
                    {form.projectType === 'fixed' && (
                      <div>
                        <div className="text-sm font-medium text-gray-500">Price</div>
                        <div className="mt-1 text-base text-gray-900">
                          {form.price ? `$${form.price.toFixed(2)} USD` : '—'}
                        </div>
                      </div>
                    )}
                    {form.projectType === 'auction' && (
                      <>
                        <div>
                          <div className="text-sm font-medium text-gray-500">Starting Bid</div>
                          <div className="mt-1 text-base text-gray-900">
                            ${form.auctionSettings?.startingBid.toFixed(2)} USD
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-500">Duration</div>
                          <div className="mt-1 text-base text-gray-900">
                            {form.auctionSettings?.duration} days
                          </div>
                        </div>
                      </>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-500">Licensing Terms</div>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center text-base text-gray-900">
                          <svg
                            className={`h-5 w-5 ${
                              form.licensing.includeSourceCode ? 'text-green-500' : 'text-gray-400'
                            }`}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d={form.licensing.includeSourceCode ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
                          </svg>
                          <span className="ml-2">Include Source Code</span>
                        </div>
                        <div className="flex items-center text-base text-gray-900">
                          <svg
                            className={`h-5 w-5 ${
                              form.licensing.transferOwnership ? 'text-green-500' : 'text-gray-400'
                            }`}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d={form.licensing.transferOwnership ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
                          </svg>
                          <span className="ml-2">Transfer Full Ownership</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSuccessState = () => {
    if (!isSuccess || !createdProjectId) return null;

    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Project Created Successfully!</h3>
            <p className="text-gray-600 mb-6">Your project has been published and is now live.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate(`/projects/${createdProjectId}`)}
                className="w-full inline-flex justify-center items-center px-6 py-3 border-2 border-primary-600 text-base font-semibold rounded-xl text-white bg-primary-600 hover:bg-primary-700 hover:border-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                View Project
              </button>
              <button
                onClick={() => {
                  setIsSuccess(false);
                  setCreatedProjectId(null);
                  setForm({
                    title: '',
                    shortDescription: '',
                    tags: [],
                    category: '',
                    thumbnail: null,
                    fullDescription: '',
                    technologies: [],
                    projectType: 'fixed',
                    revenueStatus: 'pre-revenue',
                    revenueType: undefined,
                    monthlyRevenue: undefined,
                    annualRevenue: undefined,
                    userCount: undefined,
                    ytdGrowth: undefined,
                    price: undefined,
                    auctionSettings: {
                      startingBid: 0,
                      reservePrice: undefined,
                      buyNowPrice: undefined,
                      duration: 7,
                    },
                    licensing: {
                      includeSourceCode: true,
                      transferOwnership: true,
                    },
                  });
                  setCurrentStep(1);
                }}
                className="w-full inline-flex justify-center items-center px-6 py-3 border-2 border-gray-300 text-base font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                Create Another Project
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-gray-50 to-white">
      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 backdrop-blur-sm bg-white/80">
        <div className="max-w-7xl mx-auto">
          <nav aria-label="Progress" className="py-6">
            <ol className="flex items-center justify-center gap-2 md:gap-4 px-4">
              {steps.map((step, index) => (
                <li key={step.id} className="flex-1 max-w-[200px] relative">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 flex items-center justify-center rounded-full text-base font-semibold transition-all duration-200 ${
                        currentStep >= step.id
                          ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {step.id}
                    </div>
                    <div
                      className={`mt-3 text-sm font-medium text-center transition-colors duration-200 ${
                        currentStep >= step.id ? 'text-primary-600' : 'text-gray-400'
                      }`}
                    >
                      {step.name}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`hidden md:block absolute h-1 top-6 left-[calc(50%+3rem)] right-[calc(-50%+3rem)] transition-all duration-300 ${
                          currentStep > step.id ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100"
        >
          <div className="p-6 md:p-16">
            {renderFormStep()}

            {/* Navigation Buttons */}
            <div className="mt-16 flex items-center justify-between pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 1))}
                className={`${
                  currentStep === 1 ? 'invisible' : ''
                } inline-flex items-center px-8 py-4 border-2 border-gray-300 text-base font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200`}
                disabled={isSubmitting}
              >
                ← Previous
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => console.log('Saving draft...')}
                  className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-base font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  disabled={isSubmitting}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  onClick={() => currentStep === steps.length ? handleSubmit() : setCurrentStep((prev) => Math.min(prev + 1, steps.length))}
                  className="inline-flex items-center px-8 py-4 border-2 border-primary-600 text-base font-semibold rounded-xl text-white bg-primary-600 hover:bg-primary-700 hover:border-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : currentStep === steps.length ? (
                    'Publish'
                  ) : (
                    'Next →'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}; 